import { format, formatDistanceToNow } from "date-fns";

import {
  CallSimulationJobStatus,
  type CallSimulationJobResponse,
} from "@/api/api";

import { Utility } from "./utility";

export type CallSimulationUiStatus = "idle" | "running" | "complete" | "failed";

export interface CallSimulationTranscriptEntry {
  is_agent: boolean;
  text: string;
}

export interface CallSimulationJob extends CallSimulationJobResponse {}

export class CallSimulationJob extends Utility<CallSimulationJob> {
  constructor(init?: Partial<CallSimulationJobResponse>) {
    super();
    Object.assign(this, init);
  }

  get ui_status(): CallSimulationUiStatus {
    switch (this.status) {
      case CallSimulationJobStatus.Pending:
      case CallSimulationJobStatus.Running:
      case CallSimulationJobStatus.Evaluating:
        return "running";
      case CallSimulationJobStatus.Complete:
        return "complete";
      case CallSimulationJobStatus.Failed:
        return "failed";
      default:
        return "idle";
    }
  }

  get is_non_terminal(): boolean {
    return this.ui_status === "running";
  }

  get local_created_at(): Date | null {
    if (!this.created_at) return null;
    return this.get_local_time(this.created_at);
  }

  get local_completed_at(): Date | null {
    if (!this.completed_at) return null;
    return this.get_local_time(this.completed_at);
  }

  get local_completed_at_label(): string {
    if (!this.local_completed_at) return "";
    return format(this.local_completed_at, "MMM d, yyyy, h:mm a");
  }

  get local_completed_at_relative(): string {
    if (!this.local_completed_at) return "";
    return formatDistanceToNow(this.local_completed_at, { addSuffix: true });
  }

  get quality_notes_list(): string[] {
    return this.quality_notes ?? [];
  }

  /**
   * Estimated wall-clock duration of an *equivalent real call*, in minutes, from
   * the words actually spoken by both sides of the simulation. We divide the
   * total spoken word count by an average conversational speaking rate (150 wpm
   * for English). This is a coarse signal — it assumes the mock candidate's
   * verbosity is representative — but it's grounded in the transcript rather
   * than a hand-picked formula. Returns 0 when there's no transcript yet.
   */
  get estimated_real_minutes(): number {
    const SPEAKING_RATE_WPM = 150;
    const totalWords = this.transcript_entries.reduce(
      (sum, entry) => sum + entry.text.split(/\s+/).filter(Boolean).length,
      0,
    );
    return totalWords === 0 ? 0 : Math.ceil(totalWords / SPEAKING_RATE_WPM);
  }

  /**
   * Non-null when at least one test case run finished with `status: "error"`,
   * meaning the simulation engine aborted before the conversation reached its
   * natural end (e.g. it flagged the flow as looping). The string is the raw
   * explanation surfaced for the recruiter; null means every run completed.
   */
  get early_end_explanation(): string | null {
    const runs = this.transcript ?? [];
    for (const run of runs) {
      const r = run as { status?: unknown; result_explanation?: unknown };
      if (r.status === "error") {
        return typeof r.result_explanation === "string"
          ? r.result_explanation
          : "";
      }
    }
    return null;
  }

  /**
   * Flatten the per-test-case `transcript_snapshot` into `{speaker, text}` turns.
   * The snapshot is a `{ transcript: [...], dynamicVariables, callEnded }` object where the
   * inner `transcript` array holds utterance objects (`role: "agent" | "user"` with
   * `content`) interleaved with non-utterance events (`role: "node_transition"`, tool calls)
   * that we skip. We also accept a top-level array shape defensively.
   */
  get transcript_entries(): CallSimulationTranscriptEntry[] {
    const runs = this.transcript ?? [];
    const entries: CallSimulationTranscriptEntry[] = [];
    for (const run of runs) {
      const snapshot = (run as { transcript_snapshot?: unknown })
        .transcript_snapshot;
      const utterances = Array.isArray(snapshot)
        ? snapshot
        : snapshot &&
            typeof snapshot === "object" &&
            Array.isArray((snapshot as { transcript?: unknown }).transcript)
          ? ((snapshot as { transcript: unknown[] }).transcript as unknown[])
          : [];
      for (const utterance of utterances) {
        if (!utterance || typeof utterance !== "object") continue;
        const u = utterance as Record<string, unknown>;
        const role =
          typeof u.role === "string"
            ? u.role
            : typeof u.speaker === "string"
              ? u.speaker
              : "";
        if (
          role === "node_transition" ||
          role === "tool_call_invocation" ||
          role === "tool_call_result"
        ) {
          continue;
        }
        const text =
          typeof u.content === "string"
            ? u.content
            : typeof u.text === "string"
              ? u.text
              : "";
        if (!text) continue;
        const is_agent =
          role === "agent" || role === "assistant" || role === "AI";
        entries.push({ is_agent, text });
      }
    }
    return entries;
  }
}
